import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface PlayerGroup {
    id: number;
    name: string;
    active_player_count: number;
    players: Player[];
}

interface Player {
    id: number;
    display_name: string;
    first_name?: string;
    last_name?: string;
    skill_level: string;
    skill_level_display: string;
    status: string;
    gender?: string; // 'M', 'F', 'O' or undefined
}

/**
 * pm-group-add-modal
 * 
 * Componente Lit per il modale di aggiunta giocatori da gruppo.
 * Gestisce la selezione di un gruppo, visualizzazione giocatori, selezione multipla,
 * filtro per genere basato sulle restrizioni del torneo.
 * 
 * @example
 * <pm-group-add-modal 
 *   tournament-id="123"
 *   slots-remaining="16"
 *   gender-restriction="mixed"
 *   action-url="/simulations/tournaments/123/bulk-add-existing/">
 * </pm-group-add-modal>
 */
@customElement('pm-group-add-modal')
export class PmGroupAddModal extends LitElement {
    // Properties
    @property({ type: Number, attribute: 'tournament-id' }) tournamentId: number = 0;
    @property({ type: Number, attribute: 'slots-remaining' }) slotsRemaining: number = 0;
    @property({ type: String, attribute: 'gender-restriction' }) genderRestriction: 'mixed' | 'male' | 'female' = 'mixed';
    @property({ type: String, attribute: 'action-url' }) actionUrl: string = '';

    // State
    @state() private groups: PlayerGroup[] = [];
    @state() private selectedGroupId: number | null = null;
    @state() private groupPlayers: Player[] = [];
    @state() private selectedPlayerIds: Set<number> = new Set();
    @state() private loading: boolean = false;
    @state() private error: string | null = null;

    static styles = css`
        :host {
            display: block;
        }

        .alert {
            padding: 0.75rem 1rem;
            margin-bottom: 1rem;
            border: 1px solid transparent;
            border-radius: 0.375rem;
        }

        .alert-info {
            color: #055160;
            background-color: #cff4fc;
            border-color: #b6effb;
        }

        [data-bs-theme="dark"] .alert-info {
            color: #6edff6;
            background-color: rgba(13, 202, 240, 0.15);
            border-color: rgba(13, 202, 240, 0.3);
        }

        .alert-warning {
            color: #664d03;
            background-color: #fff3cd;
            border-color: #ffecb5;
        }

        [data-bs-theme="dark"] .alert-warning {
            color: #ffda6a;
            background-color: rgba(255, 193, 7, 0.15);
            border-color: rgba(255, 193, 7, 0.3);
        }

        .form-label {
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .form-select {
            display: block;
            width: 100%;
            padding: 0.5rem 2.25rem 0.5rem 0.75rem;
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5;
            color: var(--bs-body-color);
            background-color: var(--bs-body-bg);
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }

        [data-bs-theme="dark"] .form-select {
            border-color: #495057;
        }

        .form-select:focus {
            border-color: #86b7fe;
            outline: 0;
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }

        .form-text {
            margin-top: 0.25rem;
            font-size: 0.875em;
            color: #6c757d;
        }

        .players-container {
            margin-top: 1rem;
        }

        .players-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }

        .players-header h6 {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .badge {
            display: inline-block;
            padding: 0.35em 0.65em;
            font-size: 0.75em;
            font-weight: 700;
            line-height: 1;
            color: #fff;
            text-align: center;
            white-space: nowrap;
            vertical-align: baseline;
            border-radius: 0.375rem;
        }

        .badge.bg-primary {
            background-color: #0d6efd;
        }

        .badge.bg-warning {
            background-color: #ffc107;
            color: #000;
        }

        .badge.bg-info {
            background-color: #0dcaf0;
            color: #000;
        }

        .btn-group {
            display: inline-flex;
            gap: 0;
        }

        .btn {
            display: inline-block;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5;
            text-align: center;
            text-decoration: none;
            vertical-align: middle;
            cursor: pointer;
            border: 1px solid transparent;
            border-radius: 0.375rem;
            transition: all 0.15s ease-in-out;
        }

        .btn-sm {
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
        }

        .btn-outline-primary {
            color: #0d6efd;
            border-color: #0d6efd;
        }

        .btn-outline-primary:hover {
            color: #fff;
            background-color: #0d6efd;
        }

        .btn-outline-secondary {
            color: #6c757d;
            border-color: #6c757d;
        }

        .btn-outline-secondary:hover {
            color: #fff;
            background-color: #6c757d;
        }

        .players-list {
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 0.75rem;
            max-height: 400px;
            overflow-y: auto;
        }

        [data-bs-theme="dark"] .players-list {
            border-color: #495057;
        }

        .player-item {
            padding: 0.75rem;
            border-bottom: 1px solid #dee2e6;
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        [data-bs-theme="dark"] .player-item {
            border-bottom-color: #495057;
        }

        .player-item:last-child {
            border-bottom: none;
        }

        .player-item input[type="checkbox"] {
            width: 1.25rem;
            height: 1.25rem;
            cursor: pointer;
        }

        .player-info {
            flex: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .player-name {
            font-weight: 600;
        }

        .player-skill {
            font-size: 0.875rem;
            color: #6c757d;
        }

        .gender-icon {
            margin-right: 0.25rem;
        }

        .loading-container {
            text-align: center;
            padding: 2rem;
        }

        .spinner-border {
            display: inline-block;
            width: 2rem;
            height: 2rem;
            vertical-align: text-bottom;
            border: 0.25em solid currentColor;
            border-right-color: transparent;
            border-radius: 50%;
            animation: spinner-border 0.75s linear infinite;
        }

        @keyframes spinner-border {
            to { transform: rotate(360deg); }
        }

        .empty-state {
            text-align: center;
            padding: 3rem 1rem;
            color: #6c757d;
        }

        .empty-state i {
            font-size: 3rem;
            margin-bottom: 1rem;
            opacity: 0.5;
        }

        .text-muted {
            color: #6c757d !important;
        }

        .error-message {
            color: #dc3545;
            padding: 1rem;
            text-align: center;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.loadGroups();
    }

    private async loadGroups() {
        this.loading = true;
        this.error = null;

        try {
            const response = await fetch('/simulations/api/player-groups/');
            if (!response.ok) throw new Error('Errore caricamento gruppi');
            
            const data = await response.json();
            this.groups = data.groups || [];
        } catch (error) {
            console.error('Errore caricamento gruppi:', error);
            this.error = 'Impossibile caricare i gruppi. Riprova più tardi.';
        } finally {
            this.loading = false;
        }
    }

    private async loadGroupPlayers(groupId: number) {
        this.loading = true;
        this.error = null;
        this.selectedPlayerIds.clear();

        try {
            const response = await fetch(`/simulations/api/player-groups/${groupId}/`);
            if (!response.ok) throw new Error('Errore caricamento giocatori');
            
            const group = await response.json();
            
            console.log('🔍 Group data received:', group);
            console.log('👥 Total players in group:', group.players.length);
            
            // Filtra giocatori attivi
            let players = group.players.filter((p: Player) => p.status === 'active');
            console.log('✅ Active players:', players.length);
            
            // Log gender info prima del filtro
            console.log('🚻 Gender breakdown:', {
                male: players.filter((p: Player) => p.gender === 'M').length,
                female: players.filter((p: Player) => p.gender === 'F').length,
                other: players.filter((p: Player) => p.gender === 'O').length,
                undefined: players.filter((p: Player) => !p.gender).length
            });
            
            // Applica filtro genere se necessario
            if (this.genderRestriction === 'male') {
                const beforeFilter = players.length;
                players = players.filter((p: Player) => p.gender === 'M');
                console.log(`🔵 Male filter: ${beforeFilter} → ${players.length} players`);
            } else if (this.genderRestriction === 'female') {
                const beforeFilter = players.length;
                players = players.filter((p: Player) => p.gender === 'F');
                console.log(`🔴 Female filter: ${beforeFilter} → ${players.length} players`);
            }
            
            this.groupPlayers = players;
            console.log('📋 Final players to show:', players.length);
            
            // Seleziona tutti di default
            players.forEach((p: Player) => this.selectedPlayerIds.add(p.id));
            this.requestUpdate();
            
        } catch (error) {
            console.error('Errore caricamento giocatori gruppo:', error);
            this.error = 'Impossibile caricare i giocatori del gruppo.';
        } finally {
            this.loading = false;
        }
    }

    private handleGroupChange(e: Event) {
        const select = e.target as HTMLSelectElement;
        const groupId = parseInt(select.value);
        
        if (groupId) {
            this.selectedGroupId = groupId;
            this.loadGroupPlayers(groupId);
        } else {
            this.selectedGroupId = null;
            this.groupPlayers = [];
            this.selectedPlayerIds.clear();
        }
    }

    private togglePlayer(playerId: number) {
        if (this.selectedPlayerIds.has(playerId)) {
            this.selectedPlayerIds.delete(playerId);
        } else {
            this.selectedPlayerIds.add(playerId);
        }
        this.requestUpdate();
    }

    private selectAll() {
        this.groupPlayers.forEach(p => this.selectedPlayerIds.add(p.id));
        this.requestUpdate();
    }

    private deselectAll() {
        this.selectedPlayerIds.clear();
        this.requestUpdate();
    }

    private getGenderIcon(gender?: string): string {
        if (gender === 'M') return '♂️';
        if (gender === 'F') return '♀️';
        if (gender === 'O') return '⚧';
        return '';
    }

    private handleSubmit(e: Event) {
        e.preventDefault();
        
        const selectedCount = this.selectedPlayerIds.size;
        if (selectedCount === 0) return;

        // Crea form e invia
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.actionUrl;

        // CSRF token
        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement;
        if (csrfToken) {
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = 'csrfmiddlewaretoken';
            csrfInput.value = csrfToken.value;
            form.appendChild(csrfInput);
        }

        // Player IDs (limita al massimo slot disponibili)
        const playerIds = Array.from(this.selectedPlayerIds).slice(0, this.slotsRemaining);
        playerIds.forEach(id => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'player_ids';
            input.value = id.toString();
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
    }

    private renderGenderRestrictionAlert() {
        if (this.genderRestriction === 'mixed') return null;

        return html`
            <div class="alert alert-info">
                <i class="fas fa-info-circle me-2"></i>
                ${this.genderRestriction === 'male' 
                    ? html`<strong>Torneo maschile:</strong> Solo giocatori ♂️ visibili nel gruppo`
                    : html`<strong>Torneo femminile:</strong> Solo giocatrici ♀️ visibili nel gruppo`
                }
            </div>
        `;
    }

    render() {
        const selectedCount = this.selectedPlayerIds.size;
        const actualAddCount = Math.min(selectedCount, this.slotsRemaining);
        const showWarning = selectedCount > this.slotsRemaining;

        return html`
            <!-- Info disponibilità -->
            <div class="alert alert-info">
                <i class="fas fa-info-circle me-2"></i>
                <strong>Slot disponibili:</strong> ${this.slotsRemaining} posti liberi nel torneo
            </div>

            ${this.renderGenderRestrictionAlert()}

            <!-- Selezione Gruppo -->
            <div style="margin-bottom: 1.5rem;">
                <label for="groupSelect" class="form-label">
                    <i class="fas fa-layer-group me-1"></i>Seleziona un gruppo
                </label>
                <select 
                    id="groupSelect" 
                    class="form-select"
                    @change=${this.handleGroupChange}
                    ?disabled=${this.loading}>
                    <option value="">-- Scegli un gruppo --</option>
                    ${this.groups.map(group => html`
                        <option value="${group.id}">
                            ${group.name} (${group.active_player_count} giocatori attivi)
                        </option>
                    `)}
                </select>
                <div class="form-text">I giocatori del gruppo selezionato verranno mostrati sotto</div>
            </div>

            <!-- Loading -->
            ${this.loading ? html`
                <div class="loading-container">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Caricamento...</span>
                    </div>
                    <p class="text-muted" style="margin-top: 1rem;">Caricamento giocatori...</p>
                </div>
            ` : ''}

            <!-- Error -->
            ${this.error ? html`
                <div class="error-message">
                    <i class="fas fa-exclamation-triangle"></i>
                    ${this.error}
                </div>
            ` : ''}

            <!-- Lista giocatori -->
            ${!this.loading && !this.error && this.selectedGroupId ? html`
                <div class="players-container">
                    ${this.groupPlayers.length > 0 ? html`
                        <div class="players-header">
                            <h6>
                                <i class="fas fa-users me-2"></i>Giocatori del Gruppo
                                <span class="badge ${showWarning ? 'bg-warning' : 'bg-primary'}" style="margin-left: 0.5rem;">
                                    ${selectedCount} selezionati
                                </span>
                            </h6>
                            <div class="btn-group">
                                <button 
                                    type="button" 
                                    class="btn btn-sm btn-outline-primary"
                                    @click=${this.selectAll}>
                                    <i class="fas fa-check-double me-1"></i>Tutti
                                </button>
                                <button 
                                    type="button" 
                                    class="btn btn-sm btn-outline-secondary"
                                    @click=${this.deselectAll}>
                                    <i class="fas fa-times me-1"></i>Nessuno
                                </button>
                            </div>
                        </div>

                        <div class="players-list">
                            ${this.groupPlayers.map(player => html`
                                <div class="player-item">
                                    <input 
                                        type="checkbox" 
                                        .checked=${this.selectedPlayerIds.has(player.id)}
                                        @change=${() => this.togglePlayer(player.id)}
                                        id="player_${player.id}">
                                    <label class="player-info" for="player_${player.id}">
                                        <div>
                                            <span class="gender-icon">${this.getGenderIcon(player.gender)}</span>
                                            <span class="player-name">${player.display_name}</span>
                                            <br>
                                            <span class="player-skill">${player.skill_level_display || player.skill_level}</span>
                                        </div>
                                        <span class="badge bg-info">${player.skill_level_display || player.skill_level}</span>
                                    </label>
                                </div>
                            `)}
                        </div>

                        ${showWarning ? html`
                            <div class="alert alert-warning" style="margin-top: 1rem;">
                                <i class="fas fa-exclamation-triangle me-2"></i>
                                Hai selezionato più giocatori degli slot disponibili. 
                                Verranno aggiunti solo i primi <strong>${this.slotsRemaining}</strong> giocatori.
                            </div>
                        ` : ''}

                        <!-- Submit Button -->
                        <div style="margin-top: 1.5rem; display: flex; gap: 0.5rem; justify-content: flex-end;">
                            <button 
                                type="button" 
                                class="btn btn-secondary"
                                @click=${() => this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))}>
                                <i class="fas fa-times me-2"></i>Annulla
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-success"
                                ?disabled=${selectedCount === 0}
                                @click=${this.handleSubmit}
                                style="background-color: #198754; border-color: #198754; color: white;">
                                <i class="fas fa-user-plus me-2"></i>Aggiungi ${actualAddCount} Giocatori
                            </button>
                        </div>
                    ` : html`
                        <div class="empty-state">
                            <i class="fas fa-users"></i>
                            <h5 class="text-muted">
                                ${this.genderRestriction === 'mixed' 
                                    ? 'Nessun giocatore attivo in questo gruppo'
                                    : `Nessun giocatore ${this.genderRestriction === 'male' ? 'maschio' : 'femmina'} attivo in questo gruppo`
                                }
                            </h5>
                            ${this.genderRestriction !== 'mixed' ? html`
                                <p class="text-muted">
                                    Il torneo accetta solo giocatori ${this.genderRestriction === 'male' ? '♂️ maschi' : '♀️ femmine'}
                                </p>
                            ` : ''}
                        </div>
                    `}
                </div>
            ` : ''}

            <!-- Empty state iniziale -->
            ${!this.loading && !this.error && !this.selectedGroupId ? html`
                <div class="empty-state">
                    <i class="fas fa-layer-group"></i>
                    <h5 class="text-muted">Seleziona un gruppo</h5>
                    <p class="text-muted">Scegli un gruppo dal menu a tendina per vedere i giocatori</p>
                </div>
            ` : ''}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'pm-group-add-modal': PmGroupAddModal;
    }
}
